import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const LanguageContext = createContext();

// Default Norwegian translations as fallback
const defaultTranslations = {
  nav: {
    home: "Hjem",
    scanner: "Skann", 
    log: "Min logg",
    training_log: "Min logg", 
    profile: "Profil",
    admin: "SKYTELEDER",
    super_admin: "Super Admin",
    logout: "Logg ut",
    digital_training_log: "Digital Treningslogg"
  },
  auth: {
    login: {
      title: "Logg inn",
      email: "E-post",
      password: "Passord",
      button: "Logg inn",
      invalid_credentials: "Ugyldig e-post eller passord"
    }
  },
  messages: {
    loading: "Laster...",
    error: "Det oppstod en feil",
    success: "Vellykket",
    save: "Lagre",
    cancel: "Avbryt",
    delete: "Slett",
    edit: "Rediger",
    add: "Legg til"
  },
  training_log: {
    title: "Treningslogg",
    complete_log: "Komplett Treningslogg",
    my_log: "Min Treningslogg",
    subtitle_all: "Se treningslogger for alle medlemmer",
    subtitle_own: "Se treningslogger for dine økter",
    filter_title: "Filtrer treningslogger",
    show_filters: "Vis avanserte filtre",
    hide_filters: "Skjul filtre",
    clear_filters: "Tøm alle filtre",
    search_placeholder: "Søk i aktivitet, sted, eller skyteleder...",
    filters: {
      search: "Søk etter medlem eller aktivitet...",
      date_range: "Tidsperiode",
      branch: "Gren",
      location: "Bane",
      officer: "Skyteleder",
      status: "Status"
    },
    table: {
      date: "Dato",
      member: "Medlem",
      activity: "Aktivitet",
      location: "Bane",
      officer: "Skyteleder",
      status: "Status",
      actions: "Handlinger"
    },
    status: {
      pending: "Venter",
      approved: "Godkjent",
      rejected: "Avvist"
    }
  },
  admin: {
    title: "Administrasjon",
    subtitle: "Administrer medlemmer, treningsøkter og systeminnstillinger",
    register_training: "Registrer Trening",
    tabs: {
      overview: "Dagens",
      members: "Medlemmer",
      sessions: "Klubb-logg",
      locations: "QR-koder",
      settings: "Innstillinger",
      statistics: "Statistikk"
    },
    settings: {
      title: "Organisasjonsinnstillinger",
      basic_info: "Grunnleggende informasjon",
      language_settings: "Språkinnstillinger",
      active_language: "Aktiv språk",
      export_language: "Eksporter språkfiler",
      upload_translation: "Last opp oversettelse"
    }
  },
  profile: {
    title: "Min Profil",
    personal_info: "Personlig informasjon",
    documents: "Dokumenter", 
    profile_image: "Profilbilde",
    languageSettings: "Språkinnstillinger",
    preferredLanguage: "Foretrukket språk",
    languageChanged: "Språk endret til",
    languageError: "Kunne ikke endre språk",
    languageDescription: "Dette endrer språket kun for din brukeropplevelse. Standard språk er norsk bokmål."
  }
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('no');
  const [translations, setTranslations] = useState(defaultTranslations);
  const [loading, setLoading] = useState(false);
  const [availableLanguages, setAvailableLanguages] = useState([
    { code: 'no', name: 'Norsk (Bokmål)' }
  ]);

  const API_BASE = process.env.REACT_APP_BACKEND_URL;  // No port fallback for production

  // Load available languages for organization
  const loadAvailableLanguages = useCallback(async (organizationId, userRole) => {
    if (!organizationId) return;

    // For non-admin users, just use default languages (avoid 403 errors)
    if (!userRole || !['super_admin', 'org_admin'].includes(userRole)) {
      const defaultLanguages = [
        { code: 'no', name: 'Norsk (Bokmål)' },
        { code: 'en', name: 'English' },
        { code: 'sv', name: 'Svenska' },
        { code: 'da', name: 'Dansk' }
      ];
      setAvailableLanguages(defaultLanguages);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(
        `${API_BASE}/api/organizations/${organizationId}/language-files`, 
        { headers }
      );

      const languages = [
        { code: 'no', name: 'Norsk (Bokmål)' },
        { code: 'en', name: 'English' },
        { code: 'sv', name: 'Svenska' },
        { code: 'da', name: 'Dansk' },
        ...response.data.language_files.map(file => ({
          code: file.language_code,
          name: file.language_name
        }))
      ];

      setAvailableLanguages(languages);
    } catch (error) {
      console.error('Failed to load available languages:', error);
      // Fallback to default languages on error
      const defaultLanguages = [
        { code: 'no', name: 'Norsk (Bokmål)' },
        { code: 'en', name: 'English' },
        { code: 'sv', name: 'Svenska' },
        { code: 'da', name: 'Dansk' }
      ];
      setAvailableLanguages(defaultLanguages);
    }
  }, [API_BASE]);

  // Load language translations for specific language
  const loadLanguageTranslations = useCallback(async (organizationId, languageCode, userRole) => {
    if (!organizationId) return;

    setLoading(true);
    
    // For Norwegian or non-admin users, just use default translations
    if (languageCode === 'no' || !userRole || !['super_admin', 'org_admin'].includes(userRole)) {
      setTranslations(defaultTranslations);
      setLoading(false);
      return;
    }

    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(
        `${API_BASE}/api/organizations/${organizationId}/language-files/${languageCode}`,
        { headers }
      );

      if (response.data && response.data.translations) {
        // Merge with default translations to ensure all keys exist
        const mergedTranslations = mergeTranslations(defaultTranslations, response.data.translations);
        setTranslations(mergedTranslations);
      }
    } catch (error) {
      console.error('Failed to load language translations:', error);
      // Fall back to default translations
      setTranslations(defaultTranslations);
    } finally {
      setLoading(false);
    }
  }, [API_BASE]);

  // Deep merge translations to ensure all keys exist
  const mergeTranslations = (defaultTrans, newTrans) => {
    const merged = { ...defaultTrans };
    
    Object.keys(newTrans).forEach(key => {
      if (typeof newTrans[key] === 'object' && typeof defaultTrans[key] === 'object') {
        merged[key] = mergeTranslations(defaultTrans[key], newTrans[key]);
      } else {
        merged[key] = newTrans[key];
      }
    });
    
    return merged;
  };

  // Change language
  const changeLanguage = useCallback(async (languageCode, organizationId, userRole) => {
    setCurrentLanguage(languageCode);
    await loadLanguageTranslations(organizationId, languageCode, userRole);
    
    // Store preference in localStorage
    localStorage.setItem('preferred_language', languageCode);
  }, [loadLanguageTranslations]);

  // Get translation by key path (e.g., "nav.training_log" or "auth.login.title")
  const t = useCallback((keyPath, defaultValue = keyPath) => {
    const keys = keyPath.split('.');
    let value = translations;
    
    for (const key of keys) {
      if (value && typeof value === 'object' && value[key] !== undefined) {
        value = value[key];
      } else {
        return defaultValue;
      }
    }
    
    return typeof value === 'string' ? value : defaultValue;
  }, [translations]);

  // Initialize language and load translations when user is available
  const initializeLanguage = useCallback(async (organizationId, userRole) => {
    if (!organizationId) return;
    
    const savedLanguage = localStorage.getItem('preferred_language') || 'no';
    setCurrentLanguage(savedLanguage);
    
    // Load available languages
    await loadAvailableLanguages(organizationId, userRole);
    
    // Load translations for saved language
    if (savedLanguage !== 'no') {
      await loadLanguageTranslations(organizationId, savedLanguage, userRole);
    }
  }, [loadAvailableLanguages, loadLanguageTranslations]);

  const value = {
    currentLanguage,
    translations,
    availableLanguages,
    loading,
    changeLanguage,
    loadAvailableLanguages,
    initializeLanguage,
    t  // Translation function
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;